import React, { useContext, useState } from 'react';
import {
  IHistoryKpi,
  IKpiInput,
  useCreateKpiMutation,
  useUpdateKpiMutation
} from '../../../../generated/graphql';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import { canUserEdit } from '../../../../utils/rightUtils';
import { parseDate } from '../../../../utils/clientUtils';
import { Colors } from '../../../../utils/theme';
import InputComponent from '../../../Common/Fields/InputComponent';
import DialogBox from '../../../Common/Layout/DialogBox';
import { Box } from '@mui/material';
import EditButton from '../../../Common/Buttons/EditButton';
import CancelButton from '../../../Common/Buttons/CancelButton';
import SaveButton from '../../../Common/Buttons/SaveButton';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../../../Common/Fields/EpraDatePicker';

interface IProps {
  stockId: string;
  companyName: string;
  handleClose: (saved: boolean, isNew?: boolean, kpiInput?: IHistoryKpi) => void;
  kpiInput: IHistoryKpi | null;
}

type IErrors = {
  date: string | null;
};

const defaultErrorsObj: IErrors = {
  date: null
};

const errorTexts = {
  date: 'Date is required!'
};

const KpiForm: React.FunctionComponent<IProps> = ({
  stockId,
  companyName,
  handleClose,
  kpiInput
}: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [editable, setEditable] = useState(kpiInput === null);
  const [date, setDate] = useState<Date | null>(kpiInput ? parseDate(kpiInput.date) : new Date());
  const [eps, setEps] = useState(kpiInput?.eps || null);
  const [nav, setNav] = useState(kpiInput?.nav || null);
  const [nnnav, setNnnav] = useState(kpiInput?.nnnav || null);
  const [niy, setNiy] = useState(kpiInput?.niy || null);
  const [vacancy, setVacancy] = useState(kpiInput?.vacancy || null);
  const [costRationIncl, setCostRationIncl] = useState(kpiInput?.cost_ratio_incl || null);
  const [costRationExcl, setCostRationExcl] = useState(kpiInput?.cost_ratio_excl || null);
  const [nra, setNra] = useState(kpiInput?.nra || null);
  const [nta, setNta] = useState(kpiInput?.nta || null);
  const [ndv, setNdv] = useState(kpiInput?.ndv || null);
  const [average, setAverage] = useState(kpiInput?.average || null);
  const [createKpi, createKpiResult] = useCreateKpiMutation();
  const [updateKpi, updateKpiResult] = useUpdateKpiMutation();
  const canEdit = canUserEdit(state.user, Types.Stock);

  const isEditable = () => createKpiResult.loading || updateKpiResult.loading || !editable;

  const getNumericInputField = (
    id: string,
    label: string,
    value: number | null,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  ) => (
    <InputComponent
      id={`kpi-form-${id}`}
      label={label}
      type="number"
      value={value || ''}
      disabled={isEditable()}
      onChange={(e) => onChange(e)}
    />
  );

  const handleDateChange = (input: Date | null) => {
    if (input) {
      if (!Number.isNaN(input.getDate())) {
        setFormErrors({ ...formErrors, date: null });
      }
    } else {
      setFormErrors({ ...formErrors, date: errorTexts.date });
    }
    setDate(input);
  };

  const getCleanValue = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    e.currentTarget.value === '' ? null : Number(e.currentTarget.value);

  const handleCancel = () => {
    if (editable && kpiInput !== null) {
      setDate(parseDate(kpiInput.date));
      setEps(kpiInput.eps || null);
      setNav(kpiInput.nav || null);
      setNnnav(kpiInput.nnnav || null);
      setNiy(kpiInput.niy || null);
      setVacancy(kpiInput.vacancy || null);
      setCostRationIncl(kpiInput.cost_ratio_incl || null);
      setCostRationExcl(kpiInput.cost_ratio_excl || null);
      setNra(kpiInput.nra || null);
      setNta(kpiInput.nta || null);
      setNdv(kpiInput.ndv || null);
      setAverage(kpiInput.average || null);
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      date: null
    };

    if (!date) {
      hasErrors = true;
      errors.date = errorTexts.date;
    }

    if (hasErrors) {
      setFormErrors(errors);
    } else {
      if (kpiInput) {
        updateKpi({
          variables: {
            id: kpiInput.id,
            input: getKpiInput() as IKpiInput
          },
          update(_, { data }) {
            handleClose(true, false, data?.result as IHistoryKpi);
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: 'Kpi updated'
              }
            });
          }
        }).catch((e) => {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'error',
              text: e.message
            }
          });
        });
      } else {
        createKpi({
          variables: {
            stock_id: stockId,
            input: getKpiInput() as IKpiInput
          },
          update(_, { data }) {
            handleClose(true, true, data?.result as IHistoryKpi);
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: 'Kpi created'
              }
            });
          }
        }).catch((e) => {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'error',
              text: e.message
            }
          });
        });
      }
    }
  };

  const getKpiInput = () => ({
    date: (date as Date).toString(),
    eps,
    nav,
    nnnav,
    niy,
    vacancy,
    cost_ratio_incl: costRationIncl,
    cost_ratio_excl: costRationExcl,
    nra,
    nta,
    ndv,
    average
  });

  return (
    <DialogBox
      id="dividend-dialog"
      width={690}
      title={kpiInput ? 'Kpi Details' : 'Create Kpi'}
      onClose={handleClose}
      childrenSx={{ px: '30px', py: '20px' }}
    >
      {kpiInput && (
        <Box sx={{ textAlign: 'right', width: '100%' }}>
          <EditButton
            id="kpi-form-edit-button"
            onClick={() => setEditable(true)}
            disabled={editable || !canEdit}
            editButtonColor={editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          mt: '20px',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: '24px'
        }}
      >
        <InputComponent
          id="kpi-form-company"
          label="Company"
          value={companyName}
          disabled={true}
          additionalStyle={{ gridColumn: 'span 4' }}
        />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <EpraDatePicker
            id="kpi-date-picker"
            label="Date"
            value={date}
            disabled={isEditable()}
            onChange={(d) => handleDateChange(d)}
            errorMsg={formErrors.date}
          />
        </LocalizationProvider>
        {getNumericInputField('eps', 'Eps', eps, (e) => setEps(getCleanValue(e)))}
        {getNumericInputField('nav', 'NAV', nav, (e) => setNav(getCleanValue(e)))}
        {getNumericInputField('nnnav', 'NnnAv', nnnav, (e) => setNnnav(getCleanValue(e)))}
        <Box sx={{ gridColumn: 'span 2' }}>
          {getNumericInputField('niy', 'Niy %', niy, (e) => setNiy(getCleanValue(e)))}
        </Box>
        <Box sx={{ gridColumn: 'span 2' }}>
          {getNumericInputField('vacancy', 'Vacancy %', vacancy, (e) =>
            setVacancy(getCleanValue(e))
          )}
        </Box>
        <Box sx={{ gridColumn: 'span 2' }}>
          {getNumericInputField('cost-ratio-incl', 'Cost Ratio (Incl.)', costRationIncl, (e) =>
            setCostRationIncl(getCleanValue(e))
          )}
        </Box>
        <Box sx={{ gridColumn: 'span 2' }}>
          {getNumericInputField('cost-ratio-excl', 'Cost Ratio (Excl.)', costRationExcl, (e) =>
            setCostRationExcl(getCleanValue(e))
          )}
        </Box>
        {getNumericInputField('nra', 'NRA', nra, (e) => setNra(getCleanValue(e)))}
        {getNumericInputField('nta', 'NTA', nta, (e) => setNta(getCleanValue(e)))}
        {getNumericInputField('ndv', 'NDV', ndv, (e) => setNdv(getCleanValue(e)))}
        {getNumericInputField('average', 'Average', average, (e) => setAverage(getCleanValue(e)))}
      </Box>
      <Box
        className="centralize"
        sx={{
          gridColumn: 'span 3',
          marginTop: '50px',
          paddingBottom: '45px'
        }}
      >
        <CancelButton
          id="cancel-kpi-button"
          onClick={handleCancel}
          disabled={createKpiResult.loading || updateKpiResult.loading}
          text={kpiInput === null || editable ? 'Cancel' : 'Close'}
        />
        <SaveButton
          id="save-kpi-button"
          data-testid="save-kpi-button"
          onClick={handleSubmit}
          disabled={createKpiResult.loading || updateKpiResult.loading || !editable}
          text={kpiInput === null ? 'Create' : 'Save'}
        />
      </Box>
    </DialogBox>
  );
};

export default KpiForm;
